import { IGame, GameMeta } from './IGame';

type GameFactory = () => IGame;

interface GameEntry {
  meta: GameMeta;
  factory: GameFactory;
}

/**
 * Static registry for game plugins.
 * Games register themselves at import time via `GameRegistry.register()`.
 */
export class GameRegistry {
  private static entries: Map<string, GameEntry> = new Map();

  /** Register a game plugin */
  static register(meta: GameMeta, factory: GameFactory): void {
    if (GameRegistry.entries.has(meta.id)) {
      console.warn(`GameRegistry: game "${meta.id}" is already registered, overwriting.`);
    }
    GameRegistry.entries.set(meta.id, { meta, factory });
  }

  /** Get metadata for all registered games */
  static list(): GameMeta[] {
    return Array.from(GameRegistry.entries.values()).map(e => e.meta);
  }

  /** Get metadata for a specific game */
  static getMeta(gameId: string): GameMeta | undefined {
    return GameRegistry.entries.get(gameId)?.meta;
  }

  /** Create a new instance of a game by its ID */
  static create(gameId: string): IGame | null {
    const entry = GameRegistry.entries.get(gameId);
    if (!entry) {
      console.error(`GameRegistry: unknown game "${gameId}"`);
      return null;
    }
    return entry.factory();
  }

  /** Check if a game ID is registered */
  static has(gameId: string): boolean {
    return GameRegistry.entries.has(gameId);
  }
}
